/**
 * Money and time formatting — the design's rules, in one place so no screen can quietly break them.
 *
 *  · Money is never truncated in a table. Abbreviation exists only for summary tiles, and the full value
 *    always travels with it as a title attribute.
 *  · Always 2dp, always thousands-separated, always with the sign visible when it is negative.
 *  · Dates read "3 September 2026". Times are WAT and say so. Relative time accompanies absolute, never
 *    replaces it — "14 minutes ago" alone is unusable in an audit conversation.
 */

const NAIRA = "₦"; // ₦

export function money(amount: number, opts: { signed?: boolean } = {}): string {
  const abs = Math.abs(amount).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (amount < 0) return `−${NAIRA}${abs}`;          // U+2212 minus, not a hyphen — it aligns
  if (opts.signed && amount > 0) return `+${NAIRA}${abs}`;
  return `${NAIRA}${abs}`;
}

/** Tiles only. Never a table cell — an operator reconciling figures must see every kobo. */
export function moneyShort(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}${NAIRA}${(abs / 1_000_000).toFixed(2)}m`;
  if (abs >= 100_000) return `${sign}${NAIRA}${(abs / 1_000).toFixed(1)}k`;
  return money(amount);
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "3 September 2026" — the house date format. */
export function dateLong(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "3 September 2026, 09:02" — WAT is stated by the caller's column header or a suffix. */
export function dateTimeLong(iso: string | Date, opts: { tz?: boolean } = {}): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${dateLong(d)}, ${hh}:${mm}${opts.tz ? " WAT" : ""}`;
}

export function relative(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "";
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return dateLong(d);
}

/** Percent from basis points: 150 → "1.5%". */
export const bpsToPercent = (bps: number): string => `${(bps / 100).toFixed(bps % 100 === 0 ? 1 : 2)}%`;

export const koboToNaira = (kobo: number): number => Math.round(kobo) / 100;
